/**
 * @file RgsTransport.ts
 * @role The RGS's HTTP seam: URLs, headers, JSON encoding, and RFC 7807 ProblemDetails ->
 *       GameError mapping. This is the ONLY module in the codebase allowed to call fetch()
 *       (Invariant 16) — the policy check in scripts/check-repo-policy.mjs allowlists this file
 *       and nothing else, so the boundary is machine-enforced rather than conventional.
 * @owns The fetch implementation, request construction, and status -> error taxonomy.
 * @reads RGS HTTP responses (unknown until the caller validates them).
 * @neverDoes Validate payloads (validators.ts owns the trust boundary). Adapt the wire shape.
 *            Hold session state. Decide outcomes.
 * @invariants 16 (HTTP only here), 21 (failures surface as errors the flow can route to a
 *             terminal state, never a hang).
 * @see src/core/backend/HttpRgsClient.ts, docs/RGS_SERVER_REQUIREMENTS.md
 */
import { GameError } from '@core/errors/GameError';
import { RgsProblemDetailsSchema } from './validators';

export type RgsFailureCode = 'SESSION_CREATE_FAILED' | 'SPIN_REQUEST_FAILED';

export interface RgsTransportConfig {
  /** e.g. `https://rgs.mimetrikgames.com` — no trailing slash required. */
  baseUrl: string;
  brand: string;
  userId: string;
  fetchImpl?: typeof fetch;
  /**
   * Called when the server reports a session it no longer knows (404). The transport holds no
   * session state, so the owner clears its own — keeping "who forgets the session" in one place.
   */
  onSessionExpired?: () => void;
}

export interface RgsRequestOptions {
  failureCode: RgsFailureCode;
  body?: unknown;
  signal?: AbortSignal;
  /** Treat 404 as an answer ("no such session") rather than a failure. */
  allowNotFound?: boolean;
}

export class RgsTransport {
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly config: RgsTransportConfig) {
    const impl = config.fetchImpl ?? globalThis.fetch;
    if (!impl) {
      throw new GameError(
        'CONFIG_LOAD_FAILED',
        'No fetch implementation available for the RGS client.',
      );
    }
    this.fetchImpl = impl.bind(globalThis);
  }

  async request(
    method: 'GET' | 'POST' | 'PUT',
    path: string,
    options: RgsRequestOptions,
  ): Promise<unknown> {
    const url = `${this.config.baseUrl.replace(/\/+$/, '')}${path}`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Brand': this.config.brand,
      'X-User-Id': this.config.userId,
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    const init: RequestInit = { method, headers };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    let response: Response;
    try {
      response = await this.fetchImpl(url, init);
    } catch (error) {
      // Includes the caller aborting via SpinCallOptions.signal. Callers must reconcile session
      // history before allowing another paid request; the deployed RGS has no idempotency key.
      throw new GameError(
        'NETWORK_TIMEOUT',
        `Could not reach the game server (${method} ${path})`,
        {
          cause: error,
          recoverable: true,
        },
      );
    }

    if (response.status === 404 && options.allowNotFound) return null;
    if (!response.ok) throw await this.toGameError(response, options.failureCode);
    if (response.status === 204) return null;

    try {
      return (await response.json()) as unknown;
    } catch (error) {
      const invalidPayloadCode =
        options.failureCode === 'SPIN_REQUEST_FAILED'
          ? 'SPIN_RESPONSE_INVALID'
          : options.failureCode;
      throw new GameError(invalidPayloadCode, 'The game server returned a malformed response.', {
        cause: error,
      });
    }
  }

  /** Map the RGS's RFC 7807 ProblemDetails onto the game's error taxonomy. */
  private async toGameError(response: Response, fallbackCode: RgsFailureCode): Promise<GameError> {
    const problem = await response
      .json()
      .then((body) => RgsProblemDetailsSchema.safeParse(body))
      .catch(() => null);
    const detail = problem?.success ? problem.data.detail : undefined;
    const title = problem?.success ? problem.data.title : undefined;
    const message = detail ?? title ?? `The game server returned ${response.status}.`;

    switch (response.status) {
      case 402:
        return new GameError('INSUFFICIENT_BALANCE', message, { recoverable: true });
      case 404:
        // A session the server no longer knows: drop it so the next boot starts a fresh one.
        this.config.onSessionExpired?.();
        return new GameError('SESSION_EXPIRED', message, { recoverable: true });
      case 400:
        return new GameError(fallbackCode, message, { recoverable: true });
      default:
        return new GameError(fallbackCode, message);
    }
  }
}
